/**
 * Bridge between MSAL and the HTTP client. Keeps `lib/api` free of feature
 * imports, so there is no import cycle between the axios instance and the
 * auth store.
 */
import { InteractionRequiredAuthError } from '@azure/msal-browser'

import { apiTokenRequest, msalInstance } from '@/lib/auth/msal'

let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler
}

export function notifyUnauthorized() {
  onUnauthorized?.()
}

/**
 * `null` when auth is switched off, or there is no signed-in account yet —
 * both are normal states, not failures, so requests simply go out with no
 * `Authorization` header.
 */
export async function getEntraAccessToken(): Promise<string | null> {
  if (!msalInstance) return null

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) return null

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account })
    return result.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Silent refresh can't satisfy this alone — expired session, revoked
      // consent, step-up MFA. Only a real sign-in fixes it, so this redirects.
      await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account })
    }
    return null
  }
}
